/**
 * Синхронизация: очередь операций и очередь файлов.
 *
 * Обе идемпотентны. Операция несёт свой opId, сгенерированный на устройстве,
 * и сервер применяет её ровно один раз, сколько бы раз она ни доехала.
 * Поэтому обрыв связи посреди отправки безопасен — повторяем целиком.
 *
 * Файлы уходят вторым потоком и не блокируют операции: карточка камня должна
 * появиться на сервере раньше, чем доедет трёхмегабайтная фотография.
 */
import { db, enqueue, setting, setSetting } from './db';
import { ulid } from '@core';

export interface SyncState {
  pending: number;
  online: boolean;
  running: boolean;
  /** Сервер отверг ключ или ключа нет вовсе. Молчать об этом нельзя:
   *  без него не работают ни распознавание визитки, ни расшифровка. */
  unauthorized: boolean;
  lastOk?: string;
  lastError?: string;
}

let running = false;
const listeners = new Set<(s: SyncState) => void>();
let state: SyncState = { pending: 0, online: navigator.onLine, running: false, unauthorized: false };

export function onSync(fn: (s: SyncState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => { listeners.delete(fn); };
}

async function emit(patch: Partial<SyncState>) {
  const [ops, files] = await Promise.all([
    db.outbox.count(),
    db.media.filter((m) => !m.uploaded).count(),
  ]);
  state = { ...state, ...patch, pending: ops + files, online: navigator.onLine };
  listeners.forEach((fn) => fn(state));
}

export async function apiToken() {
  return setting<string>('apiToken', '');
}

export async function setApiToken(t: string) {
  await setSetting('apiToken', t);
  await emit({ unauthorized: !t });
}

export async function deviceId(): Promise<string> {
  let id = await setting<string>('deviceId', '');
  if (!id) {
    id = crypto.randomUUID();
    await setSetting('deviceId', id);
  }
  return id;
}

/** Короткий код устройства — попадает во временный номер камня (…-D2-0048). */
export async function deviceCode(): Promise<string> {
  let code = await setting<string>('deviceCode', '');
  if (!code) {
    const id = await deviceId();
    code = 'D' + (parseInt(id.slice(0, 4), 16) % 90 + 10);
    await setSetting('deviceCode', code);
  }
  return code;
}

export async function apiFetch(url: string, init: RequestInit = {}) {
  const token = await apiToken();
  if (!token) {
    void emit({ unauthorized: true });
    return new Response(JSON.stringify({ error: 'no key' }), { status: 401 });
  }
  const headers = { ...(init.headers as object), Authorization: `Bearer ${token}` };
  const res = await fetch(url, { ...init, headers });
  // Отказ по ключу поднимаем наверх сразу, а не оставляем сообщением
  // где-то в середине сценария.
  if (res.status === 401) void emit({ unauthorized: true });
  else if (res.ok) void emit({ unauthorized: false });
  return res;
}


/**
 * Забрать каталог с сервера.
 *
 * Без этого приложение показывает только то, что снято на самом устройстве:
 * второй телефон того же человека и коллега по каталогу видят пустоту, хотя
 * на сервере всё лежит.
 *
 * Правило слияния простое и осторожное: то, что ещё не уехало с этого
 * устройства, серверной версией НЕ перетирается. Своя несинхронизированная
 * работа всегда важнее чужой копии — иначе можно молча потерять съёмку.
 */
export async function pullCatalog(): Promise<void> {
  if (!navigator.onLine) return;

  const res = await apiFetch('/api/bootstrap');
  if (!res.ok) return;
  const d = (await res.json()) as any;

  // Записи, которые ещё в очереди на отправку, трогать нельзя.
  const queued = new Set(
    (await db.outbox.toArray())
      .map((o) => (o.data as any)?.id)
      .filter(Boolean) as string[],
  );

  await db.transaction('rw',
    [db.exhibitions, db.suppliers, db.stones, db.facts, db.voices, db.media, db.certs, db.tags, db.talks, db.tasks],
    async () => {
      for (const e of d.exhibitions ?? []) {
        if (queued.has(e.id)) continue;
        await db.exhibitions.put({
          id: e.id, code: e.code, name: e.name,
          kind: e.kind === 'building' ? 'building' : 'show',
          address: e.address ?? undefined, landmark: e.landmark ?? undefined,
          currency: e.currency ?? undefined,
          city: e.city ?? undefined, country: e.country ?? undefined,
          venue: e.venue ?? undefined,
          startsOn: e.starts_on ? String(e.starts_on).slice(0, 10) : undefined,
          createdAt: e.created_at,
        });
      }

      // Контакты группируются заранее — они придут в карточки поставщиков.
      const contactsBySupplier = new Map<string, Array<{ id: string; kind: string; value: string }>>();
      for (const c of d.supplierContacts ?? []) {
        const list = contactsBySupplier.get(c.supplier_id) ?? [];
        list.push({ id: c.id, kind: c.kind, value: c.value });
        contactsBySupplier.set(c.supplier_id, list);
      }

      for (const s of d.suppliers ?? []) {
        if (queued.has(s.id)) continue;
        if (s.removed_at) {
          await db.suppliers.delete(s.id);
          continue;
        }
        const local = await db.suppliers.get(s.id);
        await db.suppliers.put({
          id: s.id, exhibitionId: s.exhibition_id, code: s.code ?? 'SUPPLIER',
          companyName: s.company_name,
          companyNameLocal: s.company_name_local ?? undefined,
          contactPerson: s.contact_person ?? undefined,
          contactPersonLocal: s.contact_person_local ?? undefined,
          position: s.position ?? undefined,
          positionLocal: s.position_local ?? undefined,
          country: s.country ?? undefined,
          city: s.city ?? undefined, cityLocal: s.city_local ?? undefined,
          address: s.address ?? undefined, addressLocal: s.address_local ?? undefined,
          booth: s.booth ?? undefined, hall: s.hall ?? undefined,
          floor: s.floor ?? undefined, office: s.office ?? undefined,
          website: s.website ?? undefined,
          notes: s.notes ?? undefined,
          // Свои несинхронизированные контакты дороже серверной копии,
          // но серверные добавляются: их видело другое устройство.
          contacts: (() => {
            const merged = new Map((contactsBySupplier.get(s.id) ?? []).map((c) => [c.id, c]));
            for (const c of local?.contacts ?? []) merged.set(c.id, c);
            return [...merged.values()];
          })(),
          // Счётчик номеров у устройства свой: если сервер ушёл вперёд,
          // берём больший, иначе два телефона выдадут один номер.
          seq: Math.max(local?.seq ?? 0, s.seq ?? 0),
          followUp: Boolean(s.follow_up),
          createdAt: s.created_at,
        });
      }

      for (const st of d.stones ?? []) {
        if (queued.has(st.id)) continue;
        // Убранное на другом устройстве должно исчезнуть и здесь. Это
        // единственный случай, когда синхронизация что-то удаляет локально:
        // сервер прямо сказал, что камня в каталоге больше нет.
        if (st.removed_at) {
          await db.stones.delete(st.id);
          continue;
        }
        const local = await db.stones.get(st.id);
        await db.stones.put({
          id: st.id, sessionId: st.session_id ?? undefined,
          parcelId: st.parcel_id ?? undefined,
          localNo: st.local_no ?? st.public_no ?? st.id.slice(-4),
          publicNo: st.public_no ?? undefined,
          kind: st.kind === 'parcel' ? 'parcel' : 'stone',
          supplierId: st.supplier_id, exhibitionId: st.exhibition_id,
          supplierSku: st.supplier_sku ?? undefined,
          status: local?.status ?? st.status,
          inCatalog: Boolean(st.in_catalog),
          promo: Boolean(st.promo),
          capturedAt: st.captured_at ?? st.created_at,
          createdAt: st.created_at,
        });
      }

      for (const f of d.facts ?? []) {
        if (await db.facts.get(f.id)) continue;
        await db.facts.put({
          id: f.id, subjectId: f.subject_id, attribute: f.attribute,
          valueText: f.value_text, valueNum: f.value_num == null ? null : Number(f.value_num),
          unit: f.unit, sourceType: f.source_type, confidence: f.confidence,
          sourceRef: f.source_ref ?? null, needsReview: f.needs_review,
          recordedAt: f.recorded_at,
        });
      }

      for (const v of d.voiceNotes ?? []) {
        if (queued.has(v.id)) continue;
        // Текст заметки живёт дольше самой записи: его расшифровывают на
        // сервере и правят руками с другого телефона. Пропускать уже
        // известную заметку значило бы навсегда остаться с «ждёт связи».
        const mine = await db.voices.get(v.id);
        if (mine && mine.transcript && !v.transcript) continue;
        await db.voices.put({
          id: v.id, subjectId: v.subject_id,
          subjectKind: (v.subject_kind ?? 'stone') as 'stone' | 'group' | 'session',
          subjectIds: v.subject_ids ?? undefined,
          mediaId: v.media_id ?? '',
          photoMediaId: v.photo_media_id ?? undefined,
          transcript: v.transcript ?? undefined,
          status: v.transcript_status === 'done' ? 'done'
                : v.transcript_status === 'failed' ? 'failed' : 'pending',
          durationMs: v.duration_ms ?? undefined,
          createdAt: v.created_at,
        });
      }

      for (const c of d.certificates ?? []) {
        if (queued.has(c.id)) continue;
        await db.certs.put({
          id: c.id, stoneId: c.stone_id,
          labCode: c.lab_code, labName: c.raw?.lab_name ?? null,
          reportNo: c.report_no, reportDate: c.report_date ? String(c.report_date).slice(0, 10) : null,
          raw: c.raw ?? null, uncertain: c.uncertain ?? [],
          verifiedAt: c.verified_at, verifyMethod: c.verify_method,
          verifyUrl: c.verify_url ?? null,
          scanStatus: c.scan_status ?? 'done',
          scanError: c.scan_error ?? null,
          createdAt: c.created_at,
        });
      }

      // Словарь видов приходит целиком: он маленький и общий на пространство.
      for (const t of d.tags ?? []) {
        if (queued.has(t.id)) continue;
        await db.tags.put({
          id: t.id, label: t.label, sort: t.sort ?? 100,
          hidden: Boolean(t.hidden), createdAt: t.created_at,
        });
      }

      // Беседы. Идущую на этом телефоне не трогаем: у него реплики свежее.
      for (const c of d.conversations ?? []) {
        if (queued.has(c.id)) continue;
        if (c.removed_at) { await db.talks.delete(c.id); continue; }
        const mine = await db.talks.get(c.id);
        if (mine?.dirty) continue;
        await db.talks.put({
          id: c.id, exhibitionId: c.exhibition_id ?? undefined, supplierId: c.supplier_id ?? undefined,
          mineLang: c.mine_lang, theirsLang: c.theirs_lang,
          lines: Array.isArray(c.lines) ? c.lines : [],
          startedAt: c.started_at, endedAt: c.ended_at ?? undefined,
          tzOffset: c.tz_offset ?? undefined,
          digest: c.digest ?? undefined, digestAt: c.digest_at ?? undefined,
          digestError: c.digest_error ?? undefined,
        });
      }

      // Задачи: предложенные приходят с сервера, своё неотправленное не трогаем.
      // Предложение, которого на сервере больше нет, заменено новым разбором.
      const serverTasks = new Set((d.tasks ?? []).map((t: any) => t.id));
      if (Array.isArray(d.tasks)) {
        const stale = (await db.tasks.where('status').equals('proposed').toArray())
          .filter((t) => !serverTasks.has(t.id) && !queued.has(t.id));
        await db.tasks.bulkDelete(stale.map((t) => t.id));
      }
      for (const t of d.tasks ?? []) {
        if (queued.has(t.id)) continue;
        await db.tasks.put({
          id: t.id, title: t.title, dueOn: t.due_on ? String(t.due_on).slice(0, 10) : undefined,
          status: t.status, source: t.source === 'ai' ? 'ai' : 'manual',
          supplierId: t.supplier_id ?? undefined, conversationId: t.conversation_id ?? undefined,
          createdAt: t.created_at, doneAt: t.done_at ?? undefined,
        });
      }

      // Файлы не качаем: их отдаёт сервер по ссылке, когда они нужны.
      // Тянуть на телефон чужие снимки заранее — верный способ его забить.
      for (const m of d.media ?? []) {
        const mine = await db.media.get(m.id);
        if (mine) {
          // Файл тот же, но хозяин мог смениться — после склейки стендов
          // визитка переезжает к тому, в кого влили.
          if (mine.subjectId !== m.subject_id) await db.media.update(m.id, { subjectId: m.subject_id });
          continue;
        }
        await db.media.put({
          id: m.id, subjectId: m.subject_id, role: m.role,
          blob: new Blob(), mime: m.mime ?? 'image/jpeg', bytes: m.bytes ?? 0,
          uploaded: true, serverUrl: `/media/${m.path}`,
          createdAt: m.created_at,
        });
      }
    });
}

/**
 * Беседы уходят снимком целиком.
 *
 * Пока разговор идёт, экран только пишет реплики в базу телефона и ставит
 * пометку dirty; здесь снимок встаёт в очередь. Так беседа доедет до сервера,
 * даже если экран закрыли на полуслове или телефон выключился: пометка
 * переживёт перезапуск. Одна транзакция — чтобы реплика, записанная ровно в
 * этот момент, не потеряла свою пометку.
 */
async function flushTalks(): Promise<void> {
  await db.transaction('rw', db.talks, db.outbox, async () => {
    const dirty = await db.talks.filter((t) => Boolean(t.dirty)).toArray();
    for (const { dirty: _, ...snap } of dirty) {
      await enqueue('talk.upsert', snap, ulid());
      await db.talks.update(snap.id, { dirty: false });
    }
  });
}

/**
 * Досылка потерянного.
 *
 * Камень, который сервер принял, получает от него постоянный номер. Если
 * номера нет, а камень снят давно — значит, он до сервера не доехал: так
 * случилось у заказчика с двумя камнями, чья операция упала, а телефон её
 * стёр. Снимки и заметки к ним на сервере уже лежат и прирастут к камню
 * сами, как только он появится.
 *
 * Проверка дешёвая и безопасная: досылаем только то, у чего никогда не было
 * постоянного номера. Убранное в корзину и удалённое сюда не попадёт — у них
 * номер есть.
 */
async function resendLost(): Promise<void> {
  const queued = new Set((await db.outbox.toArray()).map((o) => (o.data as any)?.id).filter(Boolean));
  const minutesAgo = Date.now() - 5 * 60_000;

  const lost = (await db.stones.toArray()).filter((st) =>
    !st.publicNo && !queued.has(st.id) && new Date(st.createdAt).getTime() < minutesAgo);
  if (!lost.length) return;

  for (const st of lost) await enqueue('stone.upsert', { ...st }, ulid());
  await emit({});
}

/** Один проход синхронизации. Безопасно звать сколь угодно часто. */
export async function syncNow(): Promise<void> {
  if (running || !navigator.onLine) return;
  running = true;
  await emit({ running: true, lastError: undefined });

  try {
    await flushTalks();

    // 1. Операции — пачками, чтобы одна большая очередь не уходила в один запрос.
    //
    // Отвергнутые сервером откладываем до следующего захода: они остаются
    // в очереди, но не держат за собой всё остальное. Иначе одна испорченная
    // запись остановила бы отправку целого дня съёмки.
    const stuck = new Set<string>();
    for (;;) {
      const batch = (await db.outbox.orderBy('createdAt').toArray())
        .filter((b) => !stuck.has(b.opId))
        .slice(0, 40);
      if (!batch.length) break;

      const res = await apiFetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: await deviceId(),
          ops: batch.map((b) => ({ opId: b.opId, kind: b.kind, data: b.data })),
        }),
      });
      if (!res.ok) throw new Error(`sync ${res.status}`);
      const out = (await res.json()) as { results?: Record<string, any> };

      /*
       * Разбираем пачку по одной операции.
       *
       * Раньше вся пачка удалялась из очереди, если ответ пришёл с кодом 200,
       * — и неважно, что сервер написал внутри. А внутри бывает отказ: у
       * заказчика так пропали два камня, у которых сервер не смог присвоить
       * номер. Фотографии и заметки к ним доехали, а самих камней на сервере
       * нет: телефон считал операцию отправленной и стёр её.
       *
       * Теперь удаляем только то, что сервер принял. Отказ остаётся в очереди
       * и уезжает снова — причину чинят на сервере, а запись ждёт.
       */
      const accepted: string[] = [];
      for (const b of batch) {
        const r = out.results?.[b.opId];
        if (b.kind === 'stone.upsert' && r?.publicNo) {
          await db.stones.update((b.data as any).id, { publicNo: r.publicNo });
        }
        if (r && r.ok === false) {
          stuck.add(b.opId);
          await db.outbox.update(b.opId, {
            tries: (b.tries ?? 0) + 1,
            lastError: String(r.error ?? 'сервер не принял запись'),
          });
          continue;
        }
        accepted.push(b.opId);
      }
      await db.outbox.bulkDelete(accepted);
      await emit({});
    }

    // 2. Файлы — по одному, чтобы на слабом Wi-Fi не забивать канал.
    for (;;) {
      const next = await db.media.filter((m) => !m.uploaded).first();
      if (!next) break;

      const form = new FormData();
      form.append('id', next.id);
      form.append('subjectId', next.subjectId);
      form.append('subjectType', 'stone');
      form.append('role', next.role);
      const ext = next.mime.includes('webm') ? 'webm'
        : next.mime.includes('mp4') ? 'mp4'
        : next.mime.includes('png') ? 'png'
        : next.mime.includes('webp') ? 'webp' : 'jpg';
      form.append('file', next.blob, `${next.id}.${ext}`);

      const res = await apiFetch('/api/media', { method: 'POST', body: form });
      if (!res.ok) throw new Error(`upload ${res.status}`);
      const saved = (await res.json()) as { url: string };

      await db.media.update(next.id, { uploaded: true, serverUrl: saved.url });
      await emit({});
    }

    // Отправили своё — забираем то, что появилось у остальных.
    await pullCatalog();
    await resendLost();

    await emit({ running: false, lastOk: new Date().toISOString() });
  } catch (e: any) {
    await emit({ running: false, lastError: String(e?.message ?? e) });
  } finally {
    running = false;
  }
}

/**
 * На Android есть фоновая синхронизация, на iOS её нет вовсе. Поэтому
 * единственный надёжный способ — синхронизировать, пока приложение открыто:
 * по таймеру, при возврате сети и при возвращении на экран.
 */
export function startSyncLoop() {
  void apiToken().then((t) => emit({ unauthorized: !t }));
  const kick = () => void syncNow();
  window.addEventListener('online', () => { void emit({}); kick(); });
  window.addEventListener('offline', () => void emit({}));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) kick(); });
  setInterval(kick, 25_000);
  kick();
}
